import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	getParents,
	hide,
	htmlToElement,
	expressQuerySelectorAll,
	expressRemoveClass,
	show,
} from "../../common/html";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { createSafeCssClassName } from "../../utils/string";
import { getPopupHtml } from "../../services/popup-service";
import { IPopup, IPopupOptions } from "../technical/popup-new";

let fetchingPopupContent = false;

// I've duplicated some code but when all popups are converted to the new layout popup from BP2.0 the code in wwwroot/ts/components/technical/popup-new.ts will be deleted
export async function createPopupLayout(options: IPopupOptions): Promise<IPopup> {
	let isOpen = false;
	let popupEl: HTMLElement;
	const eventDisposables: IDisposable = compositeDisposable(undefined);
	const html = expressQuerySelector(document, "html", true);
	const body = expressQuerySelector(document, "body", true);
	const popupContainerSelector = ".l-popup";
	const popupContentSelector = ".technical-popup-content";
	const popupCloseSelector = ".technical-close-icon";
	const popupOpenClass = "l-popup--open";
	let currentY = window.scrollY;
	const renderedDialogEl = expressQuerySelector(document, ".renderedDialogs", true);

	const {
		closeOnClickOutsidePopup = true,
		onClose,
		onBeforeOpen,
		onBeforeClose,
		onBeforeCloseAsync,
		onOpen,
		dialogId,
		cssClass,
		view,
		forceRefresh,
		data,
		closeOnEnter,
		disposeOnClose,
		customHtml,
	} = options;

	const safeDialogId = createSafeCssClassName(dialogId || "");

	const createPopupFromHtml = (res: string) => {
		if (!res) return Promise.resolve(undefined);

		const popup = renderedDialogEl.appendChild(htmlToElement(res)) as HTMLElement;

		if (safeDialogId) popup.setAttribute("id", safeDialogId);
		if (cssClass) expressAddClass(popup, cssClass);

		return Promise.resolve(popup);
	};

	const create = async (): Promise<HTMLElement | undefined> => {
		if (safeDialogId) {
			const popup = expressQuerySelector<HTMLElement>(document, "#" + safeDialogId, false);
			if (popup) {
				if (forceRefresh) {
					popup.remove();
				} else {
					popup.parentNode.appendChild(popup); // move to the end of the dom tree, so it will be above other popups with the same z-index
					return Promise.resolve(popup);
				}
			}
		}

		if (fetchingPopupContent) return Promise.resolve(undefined);
		fetchingPopupContent = true;

		// custom Html provided > use that instead of fetching from the server
		if (customHtml) {
			try {
				return await createPopupFromHtml(customHtml);
			} finally {
				fetchingPopupContent = false;
			}
		}

		// Get the html from the server
		try {
			const popupHtml = await getPopupHtml(view || "", data);
			return await createPopupFromHtml(popupHtml);
		} finally {
			fetchingPopupContent = false;
		}
	};

	const replaceContent = (content: Node | string) => {
		eventDisposables.dispose();
		const popupContentEl = expressQuerySelector<HTMLElement>(popupEl, popupContentSelector, true);
		popupContentEl.replaceWith(content);
		addOnClose();
	};

	const hasOtherOpenPopup = () => {
		// Check if we have old BP popups open
		const oldPopupEls = expressQuerySelectorAll<HTMLElement>(renderedDialogEl, ".m-popup");
		const oldOtherOpenedPopups = oldPopupEls.filter(
			(p) => p.id !== popupEl.id && (p.style.display === "flex" || p.classList.contains("is-popup-open")),
		);
		if (oldOtherOpenedPopups.length !== 0) return true;

		// Check if we have new BP popups open
		const popupEls = expressQuerySelectorAll<HTMLElement>(renderedDialogEl, ".l-popup");
		const otherOpenedPopups = popupEls.filter((p) => p.id !== popupEl.id && p.classList.contains("l-popup--open"));
		if (otherOpenedPopups.length !== 0) return true;
	};

	const close = async (): Promise<void> => {
		if (onBeforeClose) onBeforeClose(popupEl);
		if (onBeforeCloseAsync && !(await onBeforeCloseAsync(popupEl))) return;
		if (popupOpenClass) expressRemoveClass(popupEl, popupOpenClass);
		else hide(popupEl);
		isOpen = false;
		// Only do this if there are no other popups open
		const popupEls = expressQuerySelectorAll<HTMLElement>(renderedDialogEl, popupContainerSelector);
		const otherOpenedPopups = popupEls.filter(
			(p) => p.id !== popupEl.id && (p.style.display === "flex" || p.classList.contains(popupOpenClass)),
		);
		if (!hasOtherOpenPopup()) {
			expressRemoveClass(html, "has-open-dialog u-no-scroll");
			expressRemoveClass(body, "u-no-scroll");
			document.documentElement.style.marginTop = "0px";
			window.scroll(0, currentY);
		}
		eventDisposables.dispose();
		if (onClose)
			window.setTimeout(() => {
				if (!isOpen) onClose(popupEl);
			}, 200);

		if (disposeOnClose) dispose();
	};

	const onMouseDownEvent = async (ev) => {
		ev.stopPropagation();
		if (
			ev.target.id &&
			getParents(ev.target, "#" + popupEl.id).length < 1 &&
			getParents(ev.target, popupContainerSelector).length < 1 && // support multiple popups
			isOpen
		)
			await close();
	};

	const onKeyDownEvent = async (ev) => {
		ev.stopPropagation();
		if (ev.which === 13 || ev.keyCode === 13) if (isOpen) await close();
	};

	const onCloseEvent = async () => {
		if (isOpen) await close();
	};

	const addOnClose = () => {
		const closeButtons = expressQuerySelectorAll<HTMLElement>(popupEl, popupCloseSelector);
		closeButtons.forEach((closeButton) => {
			eventDisposables.add(expressEventListener(closeButton, "click", onCloseEvent));
		});
		if (closeOnClickOutsidePopup) {
			getParents(popupEl).forEach((el) => {
				eventDisposables.add(expressEventListener(el, "mousedown", onMouseDownEvent));
			});
		}
		if (closeOnEnter) eventDisposables.add(expressEventListener(document, "keydown", onKeyDownEvent));
	};

	const openPopupAsync = (): Promise<void> => {
		if (isOpen || !popupEl) return Promise.resolve();
		currentY = window.scrollY;
		if (onBeforeOpen) onBeforeOpen(popupEl);
		if (popupOpenClass) expressAddClass(popupEl, popupOpenClass);
		else show(popupEl);
		isOpen = true;
		addOnClose();
		document.documentElement.style.marginTop = "-" + Math.round(currentY) + "px";

		expressAddClass(html, "has-open-dialog u-no-scroll");
		expressAddClass(body, "u-no-scroll");

		if (!popupOpenClass) popupEl.style.display = "flex";
		if (isOpen && onOpen) onOpen(popupEl);

		return Promise.resolve();
	};

	if (!popupEl) popupEl = await create();

	const dispose = () => {
		popupEl.remove();
	};

	return Promise.resolve({
		popup: popupEl,
		openAsync: openPopupAsync,
		close: close,
		replaceContent,
		dispose,
	} as IPopup);
}
